"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Save, Trash2, X } from "lucide-react";
import { env } from "@/env";
import { useToast } from "@/components/ui/use-toast";
import ChangeAvatarModal from "@/components/user/change-avatar";
import { buildAvatarLink } from "@/lib/image";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";

export default function ProfilePage() {
  const { user, accessToken, updateUserDetails } = useAuth();
  const { toast } = useToast();
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bio, setBio] = useState(user?.bio || "");
  const [isSaving, setIsSaving] = useState(false);

  async function handleRemoveAvatar() {
    await fetch(`${env.NEXT_PUBLIC_API_URL}/user/remove-avatar`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((res) => {
      if (res.ok) {
        updateUserDetails({ avatar: null });

        toast({
          title: "Success",
          description: "Avatar removed successfully",
        });
      }
    });
  }

  async function handleSaveBio() {
    setIsSaving(true);
    try {
      const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/user/change-bio`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bio }),
      });

      if (res.ok) {
        updateUserDetails({ bio });
        setIsEditingBio(false);
        toast({
          title: "Success",
          description: "Bio updated successfully",
        });
      } else {
        throw new Error("Failed to update bio");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update bio. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Profile</h3>
        <p className="text-sm text-muted-foreground">
          Manage your public profile information.
        </p>
      </div>

      <hr className="border-t border-white/5" />

      <div className="flex flex-row items-center justify-between gap-3">
        <Avatar className="h-24 w-24">
          <AvatarImage src={buildAvatarLink(user!.username!)} />
          <AvatarFallback>{user!.username[0]!}</AvatarFallback>
        </Avatar>
        <div className="flex flex-row items-center space-x-2">
          <ChangeAvatarModal />
          <Button
            variant="destructive"
            className="gap-1"
            onClick={() => handleRemoveAvatar()}
          >
            <Trash2 size={12} />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">Bio</h4>
          {!isEditingBio ? (
            <Button
              variant="secondary"
              size="sm"
              className="gap-2"
              onClick={() => setIsEditingBio(true)}
            >
              <Pencil size={12} />
              Edit Bio
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setBio(user?.bio || "");
                  setIsEditingBio(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveBio}
                disabled={isSaving}
                className="gap-2"
              >
                <Save size={12} />
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>

        {isEditingBio ? (
          <Textarea
            placeholder="Write a short bio about yourself..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            className="min-h-[100px]"
          />
        ) : (
          <p className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-muted-foreground">
            {user?.bio || "No bio yet. Click edit to add one."}
          </p>
        )}
      </div>
    </div>
  );
}
